import React from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import SingleSelect from '../../components/SingleSelect'
import {
    LINK_TYPES,
    NO_LINK,
    needsSessionId,
} from '../../helpers/linkVariables'
import { FONT, TEXT } from '../../theme/tokens'
import Overline from './Overline'

/**
 * "Link variables" on the upload step of an inline event with template
 * variables: per variable, fill it from a column (None) or with a class link
 * made for each recipient. A linked variable needs no column. A master class
 * link needs the class's session id, asked for right here.
 *
 * Choices apply as they are made and the section always shows them, so
 * there is no closed popup still holding a choice (an old-app defect).
 *
 * @param {object} props
 * @param {string[]} props.variables the event's template variables
 * @param {object} props.mapping `{variable: link type}` as chosen
 * @param {object} props.mapped the real mappings (helpers/linkVariables
 *     `activeMappings`), which decide whether the session id is asked for
 * @param {string} props.sessionError why the session id is not good yet, or ''
 */
const LinkVariables = ({
    variables,
    mapping,
    mapped,
    onMappingChange,
    sessionId,
    onSessionIdChange,
    sessionError,
}) => (
    <Box sx={{ mb: 3 }} data-testid="link-variables">
        <Overline sx={{ mb: 0.5 }}>Link variables (optional)</Overline>
        <Typography variant="body2" sx={{ color: TEXT.faint, mb: 2 }}>
            A variable can be filled with a class link made for each recipient
            (found by their email or mobile number) instead of from a column. A
            linked variable needs no column in the file.
        </Typography>
        <Box sx={{ display: 'grid', gap: 1.5 }}>
            {variables.map((variable) => (
                <Box
                    key={variable}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 1,
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: TEXT.secondary,
                            fontFamily: FONT.mono,
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {variable}
                    </Typography>
                    <SingleSelect
                        label={`Fill ${variable} with`}
                        value={mapping[variable] ?? NO_LINK}
                        options={LINK_TYPES}
                        optionLabelText="label"
                        optionValueText="value"
                        handleChange={(value) =>
                            onMappingChange(variable, value)
                        }
                    />
                </Box>
            ))}
        </Box>
        {needsSessionId(mapped) && (
            <TextField
                id="master-class-session-id"
                label="Master class session ID"
                value={sessionId}
                onChange={(e) => onSessionIdChange(e.target.value)}
                required
                error={!!sessionError}
                helperText={
                    sessionError || 'The master class session the links join.'
                }
                size="small"
                fullWidth
                sx={{ mt: 2 }}
            />
        )}
    </Box>
)

LinkVariables.propTypes = {
    variables: PropTypes.arrayOf(PropTypes.string).isRequired,
    mapping: PropTypes.object.isRequired,
    mapped: PropTypes.object.isRequired,
    onMappingChange: PropTypes.func.isRequired,
    sessionId: PropTypes.string.isRequired,
    onSessionIdChange: PropTypes.func.isRequired,
    sessionError: PropTypes.string,
}

export default LinkVariables
